import React from "react";
import { ToastContainer } from "react-toastify";
import { useSelector } from "react-redux";

import ModalPublish from "../ModalPublish";
import { ConfirmationInitializationMessage } from "@components/Common/ConfirmationInitializationMessage";
import HistoryModal from "./HistoryModal";
import { ConfirmationModalArchived } from "./ConfirmationModalArchived";
import { ConfirmationModalActived } from "./ConfirmationModalActived";
import DeviceDissociateModal from "../PosDeviceSetup/DeviceDissociateModal";
import ModalAssociateStore from "./ModalAssociateStore";
import { ConfirmationDissociateProject } from "./ConfirmationDissociateProject";
import { ConfirmationModalCopied } from "./ConfirmationModalCopied";
import { ConfirmationUpdatingDataMessageModal } from "@components/Common/ConfirmationUpdatingDataMessageModal";
import { PosConfigurationModal } from "./PosConfigurationModal";
import { RootState } from "../../../../redux/store";

type ModalsContainerProps = {
    selectedRows: Record<string, any>;
    isInInitializationModalOpened: boolean;
    setIsInInitializationModalOpened: (value: boolean) => void;
    isAddNewApplicationButtonClicked: boolean;
    setIsAddNewApplicationButtonClicked: (value: boolean) => void;
    setIsEdited: (value: boolean) => void;
    isHistoryModalOpened: boolean;
    setIsHistoryModalOpened: (value: boolean) => void;
    isConfirmationModalArchived: boolean;
    setIsConfirmationModalArchived: (value: boolean) => void;
    isConfirmationModalActived: boolean;
    setIsConfirmationModalActived: (value: boolean) => void;
    isDeviceDissociateModalOpened: boolean;
    setIsDeviceDissociateModalOpened: (value: boolean) => void;
    isModalAssociateStore: boolean;
    isModalDissociateOpened: boolean;
    setIsModalDissociateOpened: (value: boolean) => void;
    isConfirmationModalCopied: boolean;
    setIsConfirmationModalCopied: (value: boolean) => void;
    isActionsClicked: boolean;
    setIsActionsClicked: (value: boolean) => void;
    validationAction: (() => void) | boolean;
    actions: (() => void) | boolean;
    isModesModalPublishOpened: boolean;
    setIsModesModalPublishOpened: (value: boolean) => void;
    isDataUpdated: boolean | number;
    setIsDataUpdated: (value: boolean | number) => void;
};

const ModalsContainer = ({
    selectedRows,
    isInInitializationModalOpened,
    setIsInInitializationModalOpened,
    isAddNewApplicationButtonClicked,
    setIsAddNewApplicationButtonClicked,
    setIsEdited,
    isHistoryModalOpened,
    setIsHistoryModalOpened,
    isConfirmationModalArchived,
    setIsConfirmationModalArchived,
    isConfirmationModalActived,
    setIsConfirmationModalActived,
    isDeviceDissociateModalOpened,
    setIsDeviceDissociateModalOpened,
    isModalAssociateStore,
    isModalDissociateOpened,
    setIsModalDissociateOpened,
    isConfirmationModalCopied,
    setIsConfirmationModalCopied,
    isActionsClicked,
    setIsActionsClicked,
    validationAction,
    actions,
    isModesModalPublishOpened,
    setIsModesModalPublishOpened,
    isDataUpdated,
    setIsDataUpdated,
}: ModalsContainerProps) => {
    const dataUpdatedTrigger = useSelector(
        (state: RootState) => state.posConfig.dataUpdatedTrigger
    );

    React.useEffect(() => {
        if (dataUpdatedTrigger > 0) {
            setIsDataUpdated(true);
        }
    }, [dataUpdatedTrigger, setIsDataUpdated]);

    return (
        <>
            {isInInitializationModalOpened && (
                <ConfirmationInitializationMessage
                    isModalOpened={isInInitializationModalOpened}
                    setIsModalOpened={setIsInInitializationModalOpened}
                    isClicked={isAddNewApplicationButtonClicked}
                    setIsClicked={setIsAddNewApplicationButtonClicked}
                    setIsEdited={setIsEdited}
                />
            )}

            {isHistoryModalOpened && (
                <HistoryModal
                    ids={selectedRows}
                    isModalOpened={isHistoryModalOpened}
                    setIsModalOpened={setIsHistoryModalOpened}
                    setIsDataUpdated={setIsDataUpdated}
                />
            )}

            {isConfirmationModalArchived && (
                <ConfirmationModalArchived
                    isConfirmationModalArchived={isConfirmationModalArchived}
                    setIsConfirmationModalArchived={
                        setIsConfirmationModalArchived
                    }
                    selectedRows={selectedRows}
                    setIsDataUpdated={setIsDataUpdated}
                />
            )}

            {isConfirmationModalActived && (
                <ConfirmationModalActived
                    isConfirmationModalActived={isConfirmationModalActived}
                    setIsConfirmationModalActived={
                        setIsConfirmationModalActived
                    }
                    selectedRows={selectedRows}
                    setIsDataUpdated={setIsDataUpdated}
                />
            )}

            <DeviceDissociateModal
                selectedRows={selectedRows}
                setDataIsUpdated={setIsDataUpdated}
                isDeviceDissociateModalOpened={isDeviceDissociateModalOpened}
                setIsDeviceDissociateModalOpened={
                    setIsDeviceDissociateModalOpened
                }
            />

            {isModalAssociateStore && (
                <ModalAssociateStore
                    rowData={selectedRows}
                    setIsDataUpdated={setIsDataUpdated}
                />
            )}

            {isModalDissociateOpened && (
                <ConfirmationDissociateProject
                    isModalDissociateOpened={isModalDissociateOpened}
                    setIsModalDissociateOpened={setIsModalDissociateOpened}
                    selectedRows={selectedRows}
                    setIsDataUpdated={setIsDataUpdated}
                />
            )}

            {isConfirmationModalCopied && (
                <ConfirmationModalCopied
                    isConfirmationModalCopied={isConfirmationModalCopied}
                    setIsConfirmationModalCopied={setIsConfirmationModalCopied}
                    selectedRows={selectedRows}
                    setIsDataUpdated={setIsDataUpdated}
                />
            )}

            {isActionsClicked && (
                <ConfirmationUpdatingDataMessageModal
                    isModalOpened={isActionsClicked}
                    setIsModalOpened={setIsActionsClicked}
                    validationAction={validationAction}
                    actions={actions}
                    setIsEdited={setIsEdited}
                />
            )}

            {isModesModalPublishOpened && (
                <ModalPublish
                    setIsModesModalPublishOpened={setIsModesModalPublishOpened}
                    rowData={selectedRows}
                    setIsDataUpdated={setIsDataUpdated}
                />
            )}

            <PosConfigurationModal />

            <ToastContainer limit={1} />
        </>
    );
};

export default ModalsContainer;
